import { DeleteNotePermanentlyModalStore } from "@/components/modal/delete-notes-permanently/delete-notes-permanently-modal-store";
import { ShareSheetModalStore } from "@/components/modal/share-sheet/share-sheet-modal-store";
import { LeaveNoteMessageModalStore } from "@/store/pages/NotesViewPageStore/LeaveNoteMessageModalStore";
import { AddToCollectionModalStore } from "@/components/modal/add-to-collection/add-to-collection-modal-store";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { Maybe } from "@/domains/common/types";
import { INotesViewPageStore } from "@/store/pages/NotesViewPageStore/types";
import { createProxyWithIsDeleted } from "@/domains/react/useObjectAfterDeletion";
import { MountedStore } from "@/store/pages/MountedStore";
import { INoteObservable, NoteContentVersion } from "@/store/note";
import { FileUploadRejectedModalStore } from "@/components/modal/file-upload-rejected/file-upload-rejeted-modal-store";
import { ImageUploadRejectedModalStore } from "@/components/modal/image-upload-rejected/image-upload-rejected-modal-store";
import { CustomEventName } from "@/modules/custom-event";
import { useCustomEvent } from "@/modules/custom-event";
import { AsyncResult } from "@/modules/async-result/types";
import { asyncResultModule } from "@/modules/async-result";
import { BaseError } from "@/domains/errors/kinds/BaseError";
import { objectModule } from "@/modules/object";
import { NoteVersionHistoryPageStore } from "./NoteVersionHistoryPageStore";
import { FindInNoteStore } from "@/store/pages/NotesViewPageStore/FindInNoteStore";

export class NotesViewPageStore
  extends AppSubStore
  implements INotesViewPageStore<INoteObservable>
{
  tabId: string;
  noteId: string;

  deleteNotePermanentlyModal: DeleteNotePermanentlyModalStore;
  findInNote: FindInNoteStore;
  shareSheetModal: ShareSheetModalStore;
  leaveNoteMessageModal: LeaveNoteMessageModalStore;
  addToCollectionModal: AddToCollectionModalStore;
  fileUploadRejectedModal: FileUploadRejectedModalStore;
  imageUploadRejectedModal: ImageUploadRejectedModalStore;
  mountState = new MountedStore();

  // Page-scoped Note Version History store
  noteVersionHistory: NoteVersionHistoryPageStore;

  /** This is a proxy with isDeleted=true after the note is gone. */
  itemObservable: Maybe<INoteObservable>;
  couldAccessNoteOnMount: Maybe<boolean>;

  versions: NoteContentVersion[] = [];
  showVersions = false;

  /** State tracking for note hydration when note doesn't exist locally */
  noteHydrationState: AsyncResult<boolean> = asyncResultModule.setInitial();

  constructor(injectedDeps: AppSubStoreArgs & { tabId: string; noteId: string }) {
    super(injectedDeps);
    this.tabId = injectedDeps.tabId;
    this.noteId = injectedDeps.noteId;

    this.findInNote = new FindInNoteStore(this);
    this.deleteNotePermanentlyModal = new DeleteNotePermanentlyModalStore(injectedDeps, {
      onDelete: () => this.store.navigation.tabs.closeTab({ tabId: this.tabId }),
    });
    this.addToCollectionModal = new AddToCollectionModalStore(injectedDeps, {});
    this.shareSheetModal = ShareSheetModalStore.forAppStore({ appStore: this.store });
    this.fileUploadRejectedModal = new FileUploadRejectedModalStore(injectedDeps);
    this.imageUploadRejectedModal = new ImageUploadRejectedModalStore(injectedDeps);

    makeObservable(this, {
      tabId: observable,
      noteId: observable,
      setNoteId: action,

      openFileUploadRejectedModal: true,
      openImageUploadRejectedModal: true,
      findInNote: true,
      fileUploadRejectedModal: false,
      imageUploadRejectedModal: false,
      deleteNotePermanentlyModal: false,
      shareSheetModal: false,
      leaveNoteMessageModal: false,
      addToCollectionModal: false,
      mountState: false,
      noteVersionHistory: false,
      useEffects: false,
      useNoteContentDocumentUpdated: false,
      goToMention: false,
      onMentionMouseOver: false,
      onMentionMouseOut: false,
      amIMounted: computed,
      myAccount: computed,
      versions: observable,
      showVersions: observable,
      itemObservable: observable,
      couldAccessNoteOnMount: observable,
      noteHydrationState: observable,
      isUpToDate: computed,
      note: computed,
      itemExists: computed,
      showNotFound: computed,
      isLoading: computed,
      setNoteObservable: action,
      setShowVersions: action,
      setCouldAccessNoteOnMount: action,
      hydrateNoteIfNeeded: action,
    });

    // We need this to be already observable to react to changes its observable props.
    this.leaveNoteMessageModal = new LeaveNoteMessageModalStore(this);

    // Instantiate page-scoped Note Version History store
    this.noteVersionHistory = new NoteVersionHistoryPageStore(injectedDeps);

    reaction(
      () => ({ note: this.note, amIMounted: this.amIMounted }),
      ({ note: maybeNote, amIMounted }, { note: previousNote }) => {
        if (maybeNote?.id !== previousNote?.id) {
          (async () => {
            await maybeNote?.fetchNoteContentDocument();
            if (maybeNote?.id !== this.noteId) return;

            const version = maybeNote?.noteContentDocument?.data?.data;
            runInAction(() => {
              this.versions = version ? [version] : [];
            });
          })();
        }
        const note = (() => {
          if (maybeNote) return maybeNote;
          if (!previousNote || !amIMounted) return;

          return createProxyWithIsDeleted(previousNote);
        })();
        if (note && this.itemObservable?.id !== note.id) {
          note.addToRecents();
        }
        this.setNoteObservable(note);
      }
    );

    // Close version history when switching to a different note within this tab
    reaction(
      () => this.noteId,
      noteId => {
        if (
          this.noteVersionHistory.currentNoteId &&
          this.noteVersionHistory.currentNoteId !== noteId
        ) {
          this.noteVersionHistory.closeNoteVersionHistory();
        }
      }
    );

    // Guard against opening version history for invalid/fresh notes
    reaction(
      () => this.itemObservable?.isNoteContentDocumentLoaded,
      isLoaded => {
        if (!isLoaded && this.noteVersionHistory.currentNoteId) {
          this.noteVersionHistory.closeNoteVersionHistory();
        }
      }
    );

    reaction(
      () => this.itemObservable?.canAccess,
      (canAccess, previousCanAccess) => {
        const couldAccessNoteOnMount = (() => {
          // Getting access later is fine.
          if (canAccess) return true;
          // Prefer the previous value if it's already set.
          if (typeof previousCanAccess === "boolean") return previousCanAccess;
          // Either undefined (_unknown_) or false.
          return canAccess;
        })();
        this.setCouldAccessNoteOnMount(couldAccessNoteOnMount);
      }
    );

    reaction(
      () => this.note?.isDeleted === false,
      hideVersions => {
        if (hideVersions) this.setShowVersions(false);
      }
    );

    // Trigger hydration when accessing a note that doesn't exist locally
    reaction(
      () => ({
        noteId: this.noteId,
      }),
      async ({ noteId }, previous) => {
        // Only trigger on note ID changes when mounted
        if (!noteId) return;
        if (previous?.noteId === noteId) return;

        // Check if note doesn't exist locally and trigger hydration
        const note = await this.store.notes.getAsync(noteId);
        if (!note) {
          this.hydrateNoteIfNeeded(noteId);
          return;
        }

        // Reset hydration state if note exists locally
        runInAction(() => {
          this.noteHydrationState = asyncResultModule.setInitial();
        });
      }
    );
  }

  setNoteId(noteId: string) {
    this.noteId = noteId;
  }

  setShowVersions(showVersions: boolean) {
    this.showVersions = showVersions;
  }

  goToMention: INotesViewPageStore["goToMention"] = ({
    id,
    mentionKind,
    url,
    keys,
    historyVersion,
  }) => {
    this.store.navigation.goToMention({
      id,
      mentionKind,
      url,
      keys,
      activeTabId: this.tabId,
      historyVersion,
    });
  };

  onMentionMouseOver: INotesViewPageStore["onMentionMouseOver"] = () => {
    // show the mention hover
  };

  onMentionMouseOut: INotesViewPageStore["onMentionMouseOut"] = () => {
    // hide the mention hover
  };

  openFileUploadRejectedModal: INotesViewPageStore["openFileUploadRejectedModal"] = ({
    code,
    fileName,
  }) => {
    this.fileUploadRejectedModal.open({
      code,
      fileName,
    });
  };

  openImageUploadRejectedModal: INotesViewPageStore["openImageUploadRejectedModal"] = ({
    code,
    imageName,
  }) => {
    this.imageUploadRejectedModal.open({
      code,
      imageName,
    });
  };

  setNoteObservable(noteObservable?: INoteObservable) {
    this.itemObservable = noteObservable;
  }

  setCouldAccessNoteOnMount(couldAccessNoteOnMount?: boolean) {
    this.couldAccessNoteOnMount = couldAccessNoteOnMount;
  }

  async hydrateNoteIfNeeded(noteId: string) {
    // Don't hydrate if already hydrating
    if (this.noteHydrationState.loading) {
      return;
    }

    // Set loading state
    runInAction(() => {
      this.noteHydrationState = asyncResultModule.setLoading();
    });

    try {
      const success = await this.store.notes.fetch(noteId);
      const note = await this.store.notes.getAsync(noteId);

      runInAction(() => {
        this.noteHydrationState = asyncResultModule.setReady(success);
        this.setNoteObservable(note);
      });
    } catch (unknownError) {
      runInAction(() => {
        const error = unknownError as Error;
        this.noteHydrationState = asyncResultModule.setError(
          new BaseError({
            message: "[debug] [NotesViewPageStore.hydrateNoteIfNeeded] Failed to fetch note.",
            info: objectModule.safeErrorAsJson(error),
          })
        );
      });
    }
  }

  get amIMounted() {
    return this.mountState.isMounted;
  }

  get isUpToDate() {
    return this.store.sync.isUpToDate;
  }

  get myAccount() {
    return this.store.spaceAccounts.myPersonalSpaceAccount;
  }

  get note() {
    if (!this.mountState.isMounted) return;
    const noteId = this.noteId;
    if (!noteId) return;
    return this.store.notes.getNoteObservableById({ noteId });
  }

  get showNotFound() {
    if (!this.mountState.isMounted) return false;

    if (this.couldAccessNoteOnMount === false) return true;

    if (this.leaveNoteMessageModal.isModalOpen) return false;

    // Skip not found while hydrating
    if (this.noteHydrationState.loading) return false;
    return !this.itemExists;
  }

  get isLoading(): boolean {
    return this.noteHydrationState.loading;
  }

  get itemExists(): boolean {
    // Return true when it's unknown.
    if (!this.mountState.isMounted || !this.noteId) {
      return true;
    }

    // Assume exists while we're hydrating
    if (this.noteHydrationState.loading) {
      return true;
    }

    const doesNotExist = this.store.notes.doesNotExist(this.noteId);
    return doesNotExist !== true;
  }

  useEffects() {
    this.mountState.useEffects();
    this.findInNote.useEffects();
    this.useNoteContentDocumentUpdated();
    this.store.templates.initialize();
  }

  useNoteContentDocumentUpdated() {
    useCustomEvent(CustomEventName.NoteContentDocumentUpdated, event => {
      if (event.model_data.note_id === this.itemObservable?.id) {
        runInAction(() => {
          this.versions.push(event);
        });
      }
    });
  }
}
